/**
 * Typed API client. Same-origin (Vite proxies /api in dev exactly as NPM routes
 * it in production), cookie auth, no tokens in JS-accessible storage.
 */
import type { z } from 'zod';
import type {
  FeedQueryResponse, FeedQueryBody, Opportunity, OccurrenceDetail,
  MetroSummary, PlaceResult, RegisterBody,
} from '@surge/contracts';

export type Feed = FeedQueryResponse;
export type FeedParams = z.input<typeof FeedQueryBody>;
export type Opp = z.infer<typeof Opportunity>;
export type Detail = z.infer<typeof OccurrenceDetail>;
export type Metro = MetroSummary;
export type Place = PlaceResult;
export type RegisterParams = z.input<typeof RegisterBody>;

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body !== undefined ? { 'content-type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : null,
  });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new ApiError(res.status, data.error ?? 'error', data.message ?? res.statusText);
  }
  return data as T;
}

export const api = {
  register: (body: RegisterParams) =>
    call<{ user_id: string }>('POST', '/api/v1/auth/register', body),
  login: (email: string, password: string) =>
    call<{ user_id: string }>('POST', '/api/v1/auth/login', { email, password }),

  otpRequest: (phone: string) =>
    call<void>('POST', '/api/v1/auth/otp/request', { phone }),
  otpVerify: (phone: string, code: string) =>
    call<{ user_id: string; driver_mode: string }>('POST', '/api/v1/auth/otp/verify', { phone, code }),
  logout: () => call<void>('POST', '/api/v1/auth/logout'),
  authProviders: () =>
    call<{ providers: { id: 'google' | 'microsoft' | 'facebook'; label: string }[] }>('GET', '/api/v1/auth/providers'),
  forgotPassword: (email: string) =>
    call<{ accepted: boolean }>('POST', '/api/v1/auth/password/forgot', { email }),
  resetPassword: (token: string, password: string) =>
    call<{ user_id: string }>('POST', '/api/v1/auth/password/reset', { token, password }),
  verifyEmail: (token: string) =>
    call<{ verified: boolean }>('POST', '/api/v1/auth/email/verify', { token }),
  resendVerification: () =>
    call<{ accepted: boolean }>('POST', '/api/v1/auth/email/resend', {}),

  me: () => call<{
    user_id: string; display_name: string | null; age_band: string | null;
    use_case: string | null; gender: string | null; driver_mode: string;
    profile_complete: boolean; email: string | null; email_verified: boolean;
  }>('GET', '/api/v1/me'),
  updateMe: (body: {
    display_name: string; age_band?: string | null; use_case?: string | null;
    gender?: string | null; driver_mode?: string;
  }) => call<{ driver_mode: string; profile_complete: boolean }>('PATCH', '/api/v1/me', body),
  sessions: () => call<{ sessions: unknown[] }>('GET', '/api/v1/auth/sessions'),
  signOutEverywhere: () => call<void>('DELETE', '/api/v1/auth/sessions/all'),

  feedQuery: (params: FeedParams) => call<Feed>('POST', '/api/v1/feed/query', params),

  metros: () => call<{ metros: Metro[] }>('GET', '/api/v1/metros'),
  placesSearch: (q: string, bias?: { lat: number; lng: number }) => {
    const qs = new URLSearchParams({ q });
    if (bias) { qs.set('lat', String(bias.lat)); qs.set('lng', String(bias.lng)); }
    return call<{ results: Place[] }>('GET', `/api/v1/places/search?${qs}`);
  },

  occurrence: (id: string) => call<Detail>('GET', `/api/v1/occurrences/${id}`),

  createAttempt: (decision_id: string, decision_candidate_id: string) =>
    call<{
      id: string; occurrence_id: string; staging_zone_id: string | null;
      selected_at: string; expires_at: string;
    }>('POST', '/api/v1/target-attempts', { decision_id, decision_candidate_id }),
  endAttempt: (id: string, end_reason: string) =>
    call<void>('PATCH', `/api/v1/target-attempts/${id}`, { end_reason }),
  outcome: (attemptId: string, got_ride: boolean, wait_band?: string) =>
    call<{ recorded: boolean }>('POST', `/api/v1/target-attempts/${attemptId}/outcome`,
      { got_ride, wait_band }),
  crowdExiting: (occurrence_id: string) =>
    call<{ recorded: boolean } | void>('POST', '/api/v1/egress-observations',
      { occurrence_id, crowd_exiting: true }),

  deleteAccount: () => call<void>('POST', '/api/v1/privacy/delete'),
};
